import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Lock, PawPrint, Plus, UserPlus } from "lucide-react";
import { useTribeMembers, type TribeMember } from "@/hooks/useTribeMembers";
import ChildAvatar from "@/components/ChildAvatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { Subject } from "@/lib/supabase";
import {
  participantRefKey,
  type ParticipantRef,
} from "@/types/participant";

// ── Props ────────────────────────────────────────────────────────────────────

export type ParticipantPickerMode = "memory" | "collection";

interface ParticipantPickerProps {
  mode: ParticipantPickerMode;
  value: ParticipantRef[];
  onChange: (next: ParticipantRef[]) => void;
  /** Ring color around selected chips. Defaults to a warm neutral. */
  accentColor?: string;
  /** Read-only rendering — no toggles fire. */
  disabled?: boolean;
  /** Whitelist of connection IDs to display (owner is always shown in
   *  memory mode). Empty/undefined = no restriction. */
  allowedConnectionIds?: string[];
  /** Subjects (children + pets, later toys) to render as tag-able chips.
   *  When provided, the picker shows a subject lane above the owner/co-parents
   *  lane, ordered children-first. Controlled via `selectedSubjectIds` +
   *  `onSubjectIdsChange`. The DB column on collections / memory_participants
   *  is still `child_id` (legacy name) — any subject id is valid there. */
  taggableSubjects?: Subject[];
  selectedSubjectIds?: string[];
  onSubjectIdsChange?: (next: string[]) => void;
  /** Subjects that cannot be toggled (shown selected + 🔒). */
  lockedSubjectIds?: string[];
  /** Optional tooltip text for locked subjects. When set, locked chips
   *  are wrapped in a Popover showing this message on tap. */
  lockedSubjectTooltip?: (subjectName: string) => string;
  /** Pending / provisional contributors rendered alongside the active
   *  tribe list. Used by the capture flow so a contributor invited
   *  inline appears as a selected chip immediately — before their
   *  space_members row flips to status='active'. They're not returned
   *  by useTribeMembers yet (that query filters on active), so the
   *  consumer injects them here. */
  provisionalContributors?: TribeMember[];
  /** When set, a "+ Add" chip is shown that opens a popover with
   *  "Invite a person" and/or "Add a pet" action rows. Either callback
   *  may be omitted — only the provided actions are rendered. */
  onInvitePerson?: () => void;
  onAddSubject?: () => void;
}

// Visual differentiator on chip labels — pets get a paw, toys get a teddy.
// Children stay unbadged.
const SUBJECT_BADGE: Record<Subject['subject_type'], string> = {
  child: '',
  pet: ' 🐾',
  toy: ' 🧸',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function isSelected(value: ParticipantRef[], ref: ParticipantRef): boolean {
  const key = participantRefKey(ref);
  return value.some((v) => participantRefKey(v) === key);
}

function toggle(
  value: ParticipantRef[],
  ref: ParticipantRef,
): ParticipantRef[] {
  const key = participantRefKey(ref);
  const found = value.some((v) => participantRefKey(v) === key);
  if (found) return value.filter((v) => participantRefKey(v) !== key);
  return [...value, ref];
}

function memberToRef(m: TribeMember): ParticipantRef {
  return {
    kind: "connection",
    connectionId: m.connectionId,
    profileId: m.profileId,
    role: m.role,
    name: m.name,
  };
}

// ── Sub-components ───────────────────────────────────────────────────────────

function LetterAvatar({
  name,
  size = 44,
  background = "#9CA3AF",
}: {
  name: string;
  size?: number;
  background?: string;
}) {
  const letter = name.charAt(0).toUpperCase() || "?";
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        backgroundColor: background,
        color: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: Math.round(size * 0.42),
      }}
    >
      {letter}
    </div>
  );
}

interface ChipProps {
  selected: boolean;
  disabled?: boolean;
  /** Locked = always-selected + non-clickable + 🔒 next to the label. */
  locked?: boolean;
  accentColor: string;
  onClick: () => void;
  avatar: React.ReactNode;
  label: string;
  /** Optional tooltip shown on tap (wraps chip in Popover when set). */
  tooltip?: string;
}

function Chip({
  selected,
  disabled,
  locked,
  accentColor,
  onClick,
  avatar,
  label,
  tooltip,
}: ChipProps) {
  const effectivelySelected = selected || !!locked;
  const noninteractive = disabled || locked;
  const btn = (
    <button
      type="button"
      onClick={noninteractive ? undefined : onClick}
      disabled={disabled}
      className="flex flex-col items-center gap-1 transition-opacity"
      style={{
        opacity: effectivelySelected ? 1 : 0.4,
        cursor: noninteractive ? "default" : "pointer",
      }}
    >
      <div
        style={{
          borderRadius: "50%",
          padding: 2,
          boxShadow: effectivelySelected ? `0 0 0 2px ${accentColor}` : "none",
        }}
      >
        {avatar}
      </div>
      <span className="flex items-center gap-1 text-[11px] font-medium text-foreground max-w-[64px] truncate">
        {label}
        {locked && <Lock className="h-2.5 w-2.5 opacity-60 shrink-0" />}
      </span>
    </button>
  );

  if (locked && tooltip) {
    return (
      <Popover>
        <PopoverTrigger asChild>{btn}</PopoverTrigger>
        <PopoverContent className="text-xs w-52 p-2.5">{tooltip}</PopoverContent>
      </Popover>
    );
  }
  return btn;
}

// ── Main component ───────────────────────────────────────────────────────────

const DEFAULT_ACCENT = "#7A9A7C";

const ParticipantPicker = ({
  mode,
  value,
  onChange,
  accentColor = DEFAULT_ACCENT,
  disabled = false,
  allowedConnectionIds,
  taggableSubjects,
  selectedSubjectIds,
  onSubjectIdsChange,
  lockedSubjectIds,
  lockedSubjectTooltip,
  provisionalContributors,
  onInvitePerson,
  onAddSubject,
}: ParticipantPickerProps) => {
  const { t } = useTranslation();
  const { owner, coparents: allCoparents, contributors: allContributors, isLoading: loading } = useTribeMembers();

  // Apply the collection-scoped whitelist when provided. Owner is always visible
  // in memory mode regardless — collection tagging only constrains tribe chips.
  const allowed = allowedConnectionIds && allowedConnectionIds.length > 0
    ? new Set(allowedConnectionIds)
    : null;
  const coparents = allowed ? allCoparents.filter((c) => allowed.has(c.connectionId)) : allCoparents;
  // Merge provisional contributors (e.g. just-invited pending rows) into
  // the list so they render as regular contributor chips. De-dupe on
  // connectionId in case the hook catches up and returns the same row.
  const activeContributors = allowed ? allContributors.filter((c) => allowed.has(c.connectionId)) : allContributors;
  const existingIds = new Set(activeContributors.map((c) => c.connectionId));
  const provisionalExtras = (provisionalContributors ?? []).filter(
    (c) => !existingIds.has(c.connectionId),
  );
  const contributors = [...activeContributors, ...provisionalExtras];

  // Sort subjects children-first so the lane reads predictably even if pets
  // were created before any child.
  const subjects = (taggableSubjects ?? [])
    .slice()
    .sort((a, b) => (a.subject_type === b.subject_type ? 0 : a.subject_type === 'child' ? -1 : 1));
  const subjectIds = selectedSubjectIds ?? [];
  const lockedIds = new Set(lockedSubjectIds ?? []);
  const showSubjectLane = subjects.length > 0 && !!onSubjectIdsChange;

  const toggleSubject = (id: string) => {
    if (!onSubjectIdsChange) return;
    if (lockedIds.has(id)) return;
    onSubjectIdsChange(subjectIds.includes(id) ? subjectIds.filter((x) => x !== id) : [...subjectIds, id]);
  };

  // Nothing to tag if no owner yet and no tribe (e.g. brand-new account).
  // In collection mode the owner chip is suppressed, so we hide entirely
  // when there are no tribe members at all. An "+ Add" action counts as
  // *something* to render — new accounts should still see the growth-loop
  // entry point instead of an empty space.
  const hasAddActions = !!onInvitePerson || !!onAddSubject;
  const hasAnything =
    (mode === "memory" && !!owner) ||
    coparents.length > 0 ||
    contributors.length > 0 ||
    showSubjectLane ||
    hasAddActions;

  if (loading || !hasAnything) return null;

  const ownerRef: ParticipantRef | null = owner
    ? { kind: "owner", profileId: owner.profileId }
    : null;

  // Split contributors into selected (rendered inline) vs unselected
  // (offered through the "+ Add" popover).
  const selectedContributors = contributors.filter((c) =>
    isSelected(value, memberToRef(c)),
  );
  const unselectedContributors = contributors.filter(
    (c) => !isSelected(value, memberToRef(c)),
  );

  return (
    <div className="flex flex-wrap gap-3">
      {/* Subjects first — children, then pets, then (later) toys. */}
      {showSubjectLane &&
        subjects.map((subject) => {
          const selected = subjectIds.includes(subject.id);
          const locked = lockedIds.has(subject.id);
          const badge = SUBJECT_BADGE[subject.subject_type];
          return (
            <Chip
              key={subject.id}
              selected={selected}
              locked={locked}
              disabled={disabled}
              accentColor={accentColor}
              onClick={() => toggleSubject(subject.id)}
              avatar={<ChildAvatar child={subject} size={44} />}
              label={subject.name + badge}
              tooltip={locked && lockedSubjectTooltip ? lockedSubjectTooltip(subject.name) : undefined}
            />
          );
        })}

      {/* ME */}
      {mode === "memory" && owner && ownerRef && (
        <Chip
          selected={isSelected(value, ownerRef)}
          disabled={disabled}
          accentColor={accentColor}
          onClick={() => onChange(toggle(value, ownerRef))}
          avatar={<LetterAvatar name={owner.nickname} background="#6B8FB3" />}
          label={owner.nickname}
        />
      )}

      {/* Co-parents */}
      {coparents.map((cp) => {
        const ref = memberToRef(cp);
        return (
          <Chip
            key={cp.connectionId}
            selected={isSelected(value, ref)}
            disabled={disabled}
            accentColor={accentColor}
            onClick={() => onChange(toggle(value, ref))}
            avatar={<LetterAvatar name={cp.name} background="#6B8FB3" />}
            label={cp.name}
          />
        );
      })}

      {/* Selected contributors — appear inline alongside co-parents once added */}
      {selectedContributors.map((c) => {
        const ref = memberToRef(c);
        return (
          <Chip
            key={c.connectionId}
            selected
            disabled={disabled}
            accentColor={accentColor}
            onClick={() => onChange(toggle(value, ref))}
            avatar={<LetterAvatar name={c.name} background="#9CA3AF" />}
            label={c.name}
          />
        );
      })}

      {/* + Add chip — growth-loop entry point. Always shown when the
          consumer supplies an invite/subject-add callback OR when there
          are unselected contributors left to tag. The popover mixes
          "tag an existing collaborator" and "add someone new" so the
          user doesn't have to context-switch to MyTribe mid-capture. */}
      {(hasAddActions || unselectedContributors.length > 0) && (
        <AddPopover
          disabled={disabled}
          value={value}
          onChange={onChange}
          unselectedContributors={unselectedContributors}
          onInvitePerson={onInvitePerson}
          onAddSubject={onAddSubject}
        />
      )}
    </div>
  );
};

// ── + Add popover — controlled so action-row clicks auto-close ──────────────
// Tagging an existing collaborator keeps the popover open (multi-select is
// expected), but tapping Invite / Add-pet closes it because those actions
// navigate to a sheet — leaving the popover behind would feel stale.
interface AddPopoverProps {
  disabled: boolean;
  value: ParticipantRef[];
  onChange: (next: ParticipantRef[]) => void;
  unselectedContributors: TribeMember[];
  onInvitePerson?: () => void;
  onAddSubject?: () => void;
}

function AddPopover({
  disabled,
  value,
  onChange,
  unselectedContributors,
  onInvitePerson,
  onAddSubject,
}: AddPopoverProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const hasAddActions = !!onInvitePerson || !!onAddSubject;

  return (
    <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label={t("aria.add")}
              className="flex flex-col items-center gap-1 cursor-pointer disabled:cursor-default"
            >
              <div
                className="flex items-center justify-center rounded-full border-2 border-dashed border-border bg-muted/40 text-muted-foreground hover:border-foreground hover:text-foreground transition-colors"
                style={{ width: 44, height: 44 }}
              >
                <Plus className="h-4 w-4" />
              </div>
              <span className="text-[11px] font-medium text-muted-foreground max-w-[64px] truncate">
                Add
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 p-1.5">
            {unselectedContributors.length > 0 && (
              <>
                <p className="px-2 pt-1 pb-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                  Tag a collaborator
                </p>
                <div className="flex flex-col">
                  {unselectedContributors.map((c) => {
                    const ref = memberToRef(c);
                    return (
                      <button
                        key={c.connectionId}
                        type="button"
                        onClick={() => onChange(toggle(value, ref))}
                        className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-muted transition-colors"
                      >
                        <LetterAvatar name={c.name} size={28} background="#9CA3AF" />
                        <span className="flex-1 text-left truncate">{c.name}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {hasAddActions && unselectedContributors.length > 0 && (
              <div className="my-1 h-px bg-border" />
            )}

            {hasAddActions && (
              <>
                <p className="px-2 pt-1 pb-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                  Add someone new
                </p>
                <div className="flex flex-col">
                  {onInvitePerson && (
                    <button
                      type="button"
                      onClick={() => { setOpen(false); onInvitePerson(); }}
                      className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-muted transition-colors"
                    >
                      <div
                        className="flex items-center justify-center rounded-full bg-muted text-muted-foreground"
                        style={{ width: 28, height: 28 }}
                      >
                        <UserPlus className="h-3.5 w-3.5" />
                      </div>
                      <span className="flex-1 text-left">Invite a person</span>
                    </button>
                  )}
                  {onAddSubject && (
                    <button
                      type="button"
                      onClick={() => { setOpen(false); onAddSubject(); }}
                      className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-muted transition-colors"
                    >
                      <div
                        className="flex items-center justify-center rounded-full bg-muted text-muted-foreground"
                        style={{ width: 28, height: 28 }}
                      >
                        <PawPrint className="h-3.5 w-3.5" />
                      </div>
                      <span className="flex-1 text-left">Add a pet</span>
                    </button>
                  )}
                </div>
              </>
            )}
          </PopoverContent>
        </Popover>
  );
}

export default ParticipantPicker;
